/**
 * Settings view with theme selection, custom color picker, presets, and logout.
 */

import { useAuthStore } from '../store/auth';
import { useAppStore } from '../store/app';
import { useThemeStore, THEME_PRESETS, type ThemeMode } from '../store/theme';

export function SettingsView() {
  const logout = useAuthStore((s) => s.logout);
  const deferredInstallPrompt = useAppStore((s) => s.deferredInstallPrompt);
  const setInstallPrompt = useAppStore((s) => s.setInstallPrompt);
  const { mode, customTheme, setMode, setCustomTheme, applyPreset } = useThemeStore();

  const handleInstall = async () => {
    if (!deferredInstallPrompt) return;
    await deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 desktop-pb">
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
        Settings
      </h2>

      <div className="space-y-6">
        {/* Theme */}
        <section>
          <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            Theme
          </h3>
          <div className="flex gap-2">
            {(['dark', 'light', 'custom'] as ThemeMode[]).map((t) => (
              <button
                key={t}
                onClick={() => setMode(t)}
                className="flex-1 py-3 rounded-lg text-sm font-medium transition-colors capitalize"
                style={{
                  backgroundColor: mode === t ? 'var(--accent)' : 'var(--bg-tertiary)',
                  color: mode === t ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${mode === t ? 'var(--accent)' : 'var(--border-color)'}`,
                  minHeight: '44px',
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Custom theme controls */}
          {mode === 'custom' && (
            <div className="mt-4 space-y-4 rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              {/* Presets */}
              <div>
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>
                  Presets
                </label>
                <div className="flex flex-wrap gap-2">
                  {THEME_PRESETS.map((preset) => {
                    const isActive =
                      preset.colors.accent === customTheme.accent &&
                      preset.colors.background === customTheme.background &&
                      preset.colors.text === customTheme.text;
                    return (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset)}
                        title={preset.name}
                        className="relative w-10 h-10 rounded-md border-2 transition-all hover:scale-110"
                        style={{
                          backgroundColor: preset.colors.background,
                          borderColor: isActive ? preset.colors.accent : 'var(--border-color)',
                        }}
                      >
                        <span
                          className="absolute inset-1.5 rounded-sm"
                          style={{ backgroundColor: preset.colors.accent, opacity: 0.6 }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color pickers */}
              <div className="space-y-3">
                <ColorPickerRow
                  label="Accent color"
                  value={customTheme.accent}
                  onChange={(c) => setCustomTheme({ accent: c })}
                />
                <ColorPickerRow
                  label="Background color"
                  value={customTheme.background}
                  onChange={(c) => setCustomTheme({ background: c })}
                />
                <ColorPickerRow
                  label="Text color"
                  value={customTheme.text}
                  onChange={(c) => setCustomTheme({ text: c })}
                />
              </div>
            </div>
          )}
        </section>

        {/* Install */}
        {deferredInstallPrompt && (
          <section>
            <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
              App
            </h3>
            <button
              onClick={handleInstall}
              className="w-full px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors"
              style={{
                backgroundColor: 'var(--accent-soft)',
                color: 'var(--accent)',
                minHeight: '44px',
              }}
            >
              Install AnnaCalendar
            </button>
          </section>
        )}

        {/* Logout */}
        <section>
          <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            Account
          </h3>
          <button
            onClick={logout}
            className="w-full px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--danger)',
              minHeight: '44px',
            }}
          >
            Logout
          </button>
        </section>
      </div>
    </div>
  );
}

function ColorPickerRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded cursor-pointer border border-neutral-600 bg-transparent"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
          }}
          onBlur={(e) => {
            const v = e.target.value;
            if (!/^#[0-9a-fA-F]{6}$/.test(v)) onChange(value);
          }}
          className="w-24 text-sm rounded-md px-2 py-1.5 font-mono"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            minHeight: '44px',
          }}
          maxLength={7}
        />
      </div>
    </div>
  );
}
